import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans } from 'react-i18next';
import PopupServiceImg from 'Assets/layout/dashboard/popup-service.png';
import QuickButton from 'Douleutaras/components/QuickButton/QuickButton';
import DefaultModal from 'Douleutaras/components/modals/DefaultModal';
import Modal from 'Douleutaras/components/Modal/Modal';
import JOB_CANCEL_REASONS from 'Douleutaras/constants/job';
import ModalSelectionItem from 'Douleutaras/components/modals/parts/ModalSelectionItem';
import { useAppDispatch } from 'Douleutaras/redux/hooks';
import { cancelJob } from 'Douleutaras/redux/data/jobs/jobsSlice';
import { CCFModalOptions } from 'Douleutaras/types/index';
import { ActionsContainer } from 'Douleutaras/components/SimpleComponents/ActionsContainer';
import RegularButton from 'Douleutaras/components/RegularButton/RegularButton';
import { HeaderDescription, HeaderTitle, SectionWrapper } from 'Douleutaras/components/SimpleComponents';
import { useJobExtraBidState } from 'Douleutaras/hooks/jobs/jobExtraBidState';
import { requestCcfExtraBid } from 'Douleutaras/functional-components/ccf/lib/actions';
import { ExtraBidSource, useAB } from 'Douleutaras/app/lib/ab';

interface CancelModalProps {
  isOpen: boolean;
  jobId: string;
  jobTitle: string;
  options: CCFModalOptions;
  userMugshot: string;
  title: React.ReactElement;
  titleOnSuccess: React.ReactElement;
  showBackButton: boolean;
  source?: string;
  onBack(): void;
  setIsOpen(modalStatus): void;
  onOptionSelected?(userSelection): void;
}

enum CancelModalSteps {
  ASK_EXTRA_BID = 'ask_extra_bid',
  CANCEL_JOB = 'cancel_job',
  CANCEL_JOB_SUCCESS = 'cancel_job_success'
}

export const CancelModal = ({ isOpen,
                              setIsOpen,
                              jobId,
                              options = {},
                              userMugshot,
                              title,
                              titleOnSuccess,
                              showBackButton,
                              source,
                              onBack,
                              onOptionSelected }: CancelModalProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { captureExtraBid } = useAB();
  const { bidsCount, isExtraBidDisabled } = useJobExtraBidState(jobId);

  const [ showSuccessMessage, setShowSuccessMessage ] = useState<boolean>(false);
  const [ step, setStep ] = useState<CancelModalSteps>(isExtraBidDisabled ? CancelModalSteps.CANCEL_JOB : CancelModalSteps.ASK_EXTRA_BID);

  const cancelAction = async ({ reason, additionalData }) => {
    await onOptionSelected(JOB_CANCEL_REASONS[reason].slug);
    dispatch(cancelJob({ jobId, cancelReason: JOB_CANCEL_REASONS[reason].slug, additionalData }))
      .then(() => {
        setShowSuccessMessage(true);
      });
  };

  const render = () => {
    switch (step) {
      default:
      case CancelModalSteps.CANCEL_JOB:
        return (
          <div>
            { Object.keys(JOB_CANCEL_REASONS)
              .map(reasonKey => {
                const reason = JOB_CANCEL_REASONS[reasonKey];

                // If options are passed through the backend, show only enabled ones If not, show all cancel reasons.
                if (!Object.keys(options)
                  .includes(reason.slug) && Object.keys(options).length > 0) {
                  // tslint:disable-next-line:no-null-keyword
                  return null;
                }

                const cancellationData = { reason: reason.slug, additionalData: { source } };
                if (reasonKey === 'other') {
                  cancellationData.additionalData['other_cancel_reason'] = 'Cancelled through CCF';
                }

                return (
                  <ModalSelectionItem key={`cancel_${reason.slug}`}>
                    <QuickButton
                      title={reason.literal}
                      onClick={() => cancelAction(cancellationData)}
                    />
                  </ModalSelectionItem>
                );
              })
            }
          </div>
        );
      case CancelModalSteps.CANCEL_JOB_SUCCESS:
        return;
      case CancelModalSteps.ASK_EXTRA_BID:
        return (
          <>
            <SectionWrapper marginBottom={4}>
              <HeaderTitle title={<Trans>T.CCF.CANCEL-MODAL-EXTRA-BID-TITLE</Trans>} noPadding />
            </SectionWrapper>
            <SectionWrapper>
              <HeaderDescription description={<Trans>T.CCF.CANCEL-MODAL-EXTRA-BID-TEXT</Trans>} />
            </SectionWrapper>
          </>
        );
    }
  };

  const renderTitle = () => {
    switch (step) {
      default:
      case CancelModalSteps.CANCEL_JOB:
        return title;
      case CancelModalSteps.CANCEL_JOB_SUCCESS:
        return titleOnSuccess;
      case CancelModalSteps.ASK_EXTRA_BID:
        return;
    }
  };

  const renderFooter = () => {
    switch (step) {
      default:
        return;
      case CancelModalSteps.ASK_EXTRA_BID:
        return (
          <ActionsContainer>
            <>
              <RegularButton modifier="primary"
                             size="large"
                             fullWidth
                             title={<Trans>T.CCF.CANCEL-MODAL-EXTRA-BID-ACTION-1</Trans>}
                             onClick={() => {
                               // @AB PostHog event
                               requestCcfExtraBid({
                                 jobId,
                                 bidsCount,
                                 navigate,
                                 eventTracking: captureExtraBid,
                                 eventValue: ExtraBidSource.CANCELLATION_MODAL
                               });
                             }}
              />
              <RegularButton modifier="primary-outline"
                             size="large"
                             fullWidth
                             title={<Trans>T.CCF.CANCEL-MODAL-EXTRA-BID-ACTION-2</Trans>}
                             onClick={() => setStep(CancelModalSteps.CANCEL_JOB)}
              />
            </>
          </ActionsContainer>
        );
    }
  };

  const calcShowBackButton = () => {
    switch (step) {
      default:
      case CancelModalSteps.CANCEL_JOB:
        return (!showSuccessMessage && showBackButton) || !isExtraBidDisabled;
      case CancelModalSteps.ASK_EXTRA_BID:
        return (!showSuccessMessage && showBackButton);
      case CancelModalSteps.CANCEL_JOB_SUCCESS:
        return false;
    }
  };

  const calcBackButtonCallback = () => {
    if (!isExtraBidDisabled && step !== CancelModalSteps.ASK_EXTRA_BID) {
      setStep(CancelModalSteps.ASK_EXTRA_BID);

      return;
    }

    typeof onBack === 'function' && onBack();
  };

  const successNote = <Trans>T.CCF.CANCEL MODAL MODAL NOTE SUCCESS</Trans>;
  const note = showSuccessMessage ? successNote : undefined;

  return (
    <Modal isOpen={isOpen} dismissCallback={() => setIsOpen(false)}>
      <DefaultModal
        serviceIcon={PopupServiceImg}
        serviceIconAlt={<Trans>T.CCF.CANCEL MODAL IMAGE ALT TEXT</Trans>}
        title={ renderTitle() }
        note={note}
        backText={<Trans>T.CCF.CANCEL MODAL BACK BUTTON TEXT</Trans>}
        backTextAriaLabel={<Trans>T.CCF.CANCEL MODAL BACK BUTTON TEXT</Trans>}
        closeAriaLabel={<Trans>T.CCF.CANCEL MODAL CLOSE ICON LABEL</Trans>}
        closeButtonCallback={() => setIsOpen(false)}
        backButtonCallback={ calcBackButtonCallback }
        showBackButton={ calcShowBackButton() }
        userMugshot={userMugshot}
        footerDisableLine
        footer={ renderFooter() }
      >
        { render() }
      </DefaultModal>
    </Modal>
  );
};

export default CancelModal;
